/**
 * The three states every query spends time in before it has data: loading,
 * failed, and empty. The empty one guides — the demo starts with nothing, so
 * each empty screen says what to do next.
 */

import type { ReactNode } from "react";
import { ApiError } from "@/lib/api";

export function EmptyState({
  title,
  children,
  action,
}: {
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
      <p className="text-[15px] font-semibold">{title}</p>
      {children && <p className="max-w-md text-[13px] leading-relaxed text-muted">{children}</p>}
      {action && <div className="pt-2">{action}</div>}
    </div>
  );
}

/** A server message only when the person can act on it; a generic one otherwise. */
export function errorText(error: unknown, fallback = "Something went wrong. Try again."): string {
  if (!(error instanceof ApiError && (error.isUserFixable || error.status === 403 || error.status === 404)))
    return fallback;
  return error.detail;
}

export function ErrorState({ error, what = "this" }: { error: unknown; what?: string }) {
  return (
    <div role="alert" className="rounded-lg bg-err-bg px-4 py-3 text-[13px] text-err">
      Could not load {what}. {errorText(error, "The server did not answer; try again shortly.")}
    </div>
  );
}

export function Skeleton({ rows = 3, label = "Loading" }: { rows?: number; label?: string }) {
  return (
    <div role="status" aria-label={label} className="flex flex-col gap-2 p-4">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="h-4 animate-pulse rounded bg-chip" style={{ width: `${90 - i * 12}%` }} />
      ))}
    </div>
  );
}

/** Loading → error → content, the order every page checks them in. */
export function QueryState({
  query,
  what,
  children,
}: {
  query: { isPending: boolean; isError: boolean; error: unknown };
  what: string;
  children: ReactNode;
}) {
  if (query.isPending) return <Skeleton label={`Loading ${what}`} />;
  if (query.isError) return <ErrorState error={query.error} what={what} />;
  return <>{children}</>;
}
